/**
 * Layout con menú lateral
 * -----------------------------------------------------------------------------
 * El Sidenav de MDB tiene dos modos —`side` (empuja, queda fijo) y `over`
 * (se superpone sobre el contenido)— pero no cambia solo entre ellos según el
 * ancho de la pantalla. Sin esto, o el menú tapa el contenido en el móvil o
 * desaparece en el escritorio.
 *
 * Aquí se decide el modo por viewport y se mantiene al redimensionar:
 *
 *   ≥ breakpoint  modo `side`, siempre visible; el CSS desplaza el contenido
 *   < breakpoint  modo `over`, oculto; lo abre el botón de la barra superior
 *
 * Uso:
 *   <body class="layout-sidebar">
 *     <nav class="navbar owari-navbar fixed-top">
 *       <button data-mdb-toggle="sidenav" data-mdb-target="#sidenav"
 *               class="btn btn-link d-xxl-none">…</button>
 *     </nav>
 *     <nav id="sidenav" class="sidenav owari-sidenav" data-mdb-sidenav-init>…</nav>
 *     <main class="layout-main">…</main>
 *   </body>
 *
 * El breakpoint se puede cambiar con `data-company-layout-breakpoint` en el
 * elemento del sidenav; por defecto 1400, el `xxl` de Bootstrap, que es el
 * mismo que usa el CSS del layout.
 */

import { setupSidenavSearch } from './sidenav-search.js';

const DEFAULT_BREAKPOINT = 1400;

/**
 * Convierte a píxeles el valor de una custom property (`17.5rem`, `280px`).
 */
function toPixels(value) {
  const raw = String(value).trim();
  if (!raw) return null;

  const number = parseFloat(raw);
  if (Number.isNaN(number)) return null;

  if (raw.endsWith('rem')) {
    const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return Math.round(number * root);
  }

  return Math.round(number);
}

class Layout {
  constructor(element, Sidenav) {
    this.element = element;
    this.instance = Sidenav.getInstance(element) || new Sidenav(element);
    this.breakpoint =
      Number(element.getAttribute('data-company-layout-breakpoint')) || DEFAULT_BREAKPOINT;

    this.mode = null;
    this._onResize = this._onResize.bind(this);

    this._syncWidth();
    this._apply();
    window.addEventListener('resize', this._onResize);
  }

  get isWide() {
    return window.innerWidth >= this.breakpoint;
  }

  /**
   * MDB fija el ancho del panel desde JS (240 px por defecto) con un estilo en
   * línea, así que una regla CSS no lo cambia. Y ese mismo número es el que usa
   * para desplazarlo fuera de pantalla al ocultarlo: si el CSS dijera 280 y MDB
   * siguiera creyendo 240, al cerrarse quedaría una franja de 40 px asomando.
   *
   * La fuente única es la custom property `--owari-sidebar-width`, con la que el
   * CSS reserva el hueco del contenido; aquí se le pasa a MDB.
   */
  _syncWidth() {
    const declared = getComputedStyle(this.element).getPropertyValue('--owari-sidebar-width');
    const width = toPixels(declared);
    if (!width) return;

    // Se lee del getter público `options`, que combina los valores por defecto,
    // los `data-mdb-*` y los que se hayan pasado. El objeto interno `_options`
    // puede estar vacío recién construido, y comprobarlo contra él hacía que
    // esta función se saltara el ajuste y el panel se quedara en los 240 px de
    // MDB mientras el layout reservaba otro ancho.
    const current = this.instance.options;
    if (current.width === width) return;

    this.instance.update({ ...current, width });
  }

  _apply() {
    const mode = this.isWide ? 'side' : 'over';
    if (mode === this.mode) return;

    this.mode = mode;
    this.instance.changeMode(mode);

    if (this.isWide) this.instance.show();
    else this.instance.hide();
  }

  _onResize() {
    // Solo se actúa al CRUZAR el breakpoint: reaplicar en cada píxel de resize
    // reabriría el menú que el usuario acaba de cerrar.
    cancelAnimationFrame(this._frame);
    this._frame = requestAnimationFrame(() => this._apply());
  }

  dispose() {
    window.removeEventListener('resize', this._onResize);
    delete this.element.__companyLayout;
  }

  static getInstance(element) {
    return element.__companyLayout || null;
  }
}

/**
 * Inicializa los layouts con menú lateral presentes en `root`.
 *
 * @param {ParentNode} [root=document] Ámbito a recorrer.
 * @param {Object} components Componentes de MDB ({ Sidenav }).
 */
export function setupLayout(root = document, components = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  if (!components.Sidenav) return;

  root.querySelectorAll('.owari-sidenav').forEach((element) => {
    if (element.__companyLayout) return;
    element.__companyLayout = new Layout(element, components.Sidenav);
  });

  setupSidenavSearch(root);
}

export { Layout };
export default setupLayout;
